import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { accessOf } from './utils/access';
import { useAuth } from './contexts/AuthContext';
import { useViewerUsage } from './hooks/useViewerUsage';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import AcceptInvite from './pages/auth/AcceptInvite';
import Dashboard from './pages/dashboard/Dashboard';
import Projects from './pages/dashboard/Projects';
import Project from './pages/dashboard/Project';
import TeamManagement from './pages/dashboard/TeamManagement';
import SharingCenter from './pages/dashboard/SharingCenter';
import ActivityLogs from './pages/dashboard/ActivityLogs';
import Trash from './pages/dashboard/Trash';
import ReleaseNotes from './pages/dashboard/ReleaseNotes';
import ReleaseNoteDetail from './pages/dashboard/ReleaseNoteDetail';
import FileListPage from './pages/project/FileListPage';
import SplatViewer from './pages/project/SplatViewer';
import OrthoViewer from './pages/project/OrthoViewer';
import Video360Viewer from './pages/project/Video360Viewer';
import FileViewer from './pages/project/FileViewer';
import PDFViewer from './pages/project/PDFViewer';
import ImageViewer from './pages/project/ImageViewer';
import DWGViewer from './pages/project/DWGViewer';
import DWGViewerV2 from './pages/project/DWGViewerV2';
import BIMViewerV2 from './pages/project/BIMViewerV2';
import BIMViewer from './pages/project/BIMViewer';
import PointCloudViewer from './pages/project/PointCloudViewer';
import GeoCloudViewer from './pages/project/GeoCloudViewer';
import CombinedView from './pages/project/CombinedView';
import ProjectSettings from './pages/dashboard/ProjectSettings';
import Profile from './pages/dashboard/Profile';
import Upgrade from './pages/dashboard/Upgrade';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProjects from './pages/admin/AdminProjects';
import AdminUsers from './pages/admin/AdminUsers';
import AdminConversionJobs from './pages/admin/AdminConversionJobs';
import AdminConversionStats from './pages/admin/AdminConversionStats';
import AdminCostAnalysis from './pages/admin/AdminCostAnalysis';
import AdminCostCalculator from './pages/admin/AdminCostCalculator';
import AdminUserActivity from './pages/admin/AdminUserActivity';
import AdminDeletedFiles from './pages/admin/AdminDeletedFiles';
import AdminPlatformSettings from './pages/admin/AdminPlatformSettings';
import AdminReleaseNotes from './pages/admin/AdminReleaseNotes';
import AdminUserPlans from './pages/admin/AdminUserPlans';
import AdminScheduledTasks from './pages/admin/AdminScheduledTasks';
import SharedLinkPage from './pages/share/SharedLinkPage';
import ShareGate from './pages/share/ShareGate';
import PublicCombinedView from './pages/share/PublicCombinedView';
import PublicReleaseNote from './pages/share/PublicReleaseNote';
import SharedCostCalculator from './pages/share/SharedCostCalculator';
import TransferAccept from './pages/share/TransferAccept';
import UpdateToast from './components/common/UpdateToast';

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return !isAuthenticated ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

// Any admin reaches /admin; each admin page also needs its module.
const AdminRoute = ({ children, module }: { children: React.ReactNode; module?: string }) => {
  const { isAuthenticated, isStaff, hasModule, loading } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isStaff) return <Navigate to="/dashboard" replace />;
  if (module && !hasModule(module)) return <Navigate to="/admin" replace />;
  return <>{children}</>;
};

// An expired Trial-plan account (me.trialEndsAt in the past) loses access to
// the gated viewers — the backend blocks uploads on its side.
const isTrialExpired = (user: { trialEndsAt?: string | null; role?: string } | null | undefined): boolean =>
  !!user?.trialEndsAt && user.role !== 'superadmin' && new Date(user.trialEndsAt).getTime() <= Date.now();

// The Scene replaced both Smart View and Combined View; either permission opens it.
const SceneRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isTrialExpired(user)) return <Navigate to="/upgrade" replace />;
  if (!accessOf(user).scene) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// Old Smart View / Combined View links (bookmarks, emails) land on the Scene.
const RedirectToScene = () => {
  const { projectId, token } = useParams<{ projectId?: string; token?: string }>();
  const { search } = useLocation();
  const base = token ? `/share/${token}` : `/projects/${projectId}`;
  return <Navigate to={`${base}/scene${search}`} replace />;
};

const ReleaseNotesRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, user, hasModule, loading } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user?.permissions?.releaseNotesVisible && !hasModule('release-notes')) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

const App = () => {
  useViewerUsage();
  return (
    <>
    <UpdateToast />
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      <Route path="/share/release-notes/:token" element={<PublicReleaseNote />} />
      <Route path="/share/calculator/:token" element={<SharedCostCalculator />} />
      <Route path="/transfer/:token" element={<TransferAccept />} />
      <Route path="/share/:token" element={<ShareGate />}>
        <Route index element={<SharedLinkPage />} />
        <Route path="scene" element={<PublicCombinedView />} />
        <Route path="smart-view" element={<RedirectToScene />} />
        <Route path="combined-view" element={<RedirectToScene />} />
        <Route path="viewer-2d/:fileId" element={<DWGViewer />} />
        <Route path="viewer-pdf/:fileId" element={<PDFViewer />} />
        <Route path="viewer-image/:fileId" element={<ImageViewer />} />
        <Route path="viewer-3d/:fileId" element={<BIMViewerV2 />} />
        <Route path="viewer-splat/:fileId" element={<SplatViewer />} />
        <Route path="viewer-ortho/:fileId" element={<OrthoViewer />} />
        <Route path="viewer-360/:fileId" element={<Video360Viewer />} />
      </Route>

      <Route path="/login" element={
        <PublicRoute><Login /></PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute><ForgotPassword /></PublicRoute>
      } />
      {/* Not a PublicRoute: the emailed link must work in a browser that is still signed in. */}
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/register" element={
        <PublicRoute><Register /></PublicRoute>
      } />

      <Route path="/dashboard" element={
        <PrivateRoute><Dashboard /></PrivateRoute>
      } />
      <Route path="/projects" element={
        <PrivateRoute><Projects /></PrivateRoute>
      } />
      <Route path="/projects/:projectId" element={
        <PrivateRoute><Project /></PrivateRoute>
      } />

      <Route path="/projects/:projectId/settings" element={
        <PrivateRoute><ProjectSettings /></PrivateRoute>
      } />
      <Route path="/projects/:projectId/scene" element={
        <SceneRoute><CombinedView /></SceneRoute>
      } />
      <Route path="/projects/:projectId/smart-view" element={<RedirectToScene />} />
      <Route path="/projects/:projectId/combined-view" element={<RedirectToScene />} />
      <Route path="/projects/:projectId/2d" element={
        <PrivateRoute><FileListPage /></PrivateRoute>
      } />
      <Route path="/projects/:projectId/3d" element={
        <PrivateRoute><FileListPage /></PrivateRoute>
      } />
      <Route path="/projects/:projectId/documents" element={
        <PrivateRoute><FileListPage /></PrivateRoute>
      } />
      <Route path="/projects/:projectId/point-cloud" element={
        <PrivateRoute><FileListPage /></PrivateRoute>
      } />
      <Route path="/projects/:projectId/gaussian" element={
        <PrivateRoute><FileListPage /></PrivateRoute>
      } />
      <Route path="/projects/:projectId/viewer-splat/:fileId" element={
        <PrivateRoute><SplatViewer /></PrivateRoute>
      } />

      <Route path="/projects/:projectId/orthomosaic" element={
        <PrivateRoute><FileListPage /></PrivateRoute>
      } />
      <Route path="/projects/:projectId/viewer-ortho/:fileId" element={
        <PrivateRoute><OrthoViewer /></PrivateRoute>
      } />

      <Route path="/projects/:projectId/video360" element={
        <PrivateRoute><FileListPage /></PrivateRoute>
      } />
      <Route path="/projects/:projectId/viewer-360/:fileId" element={
        <PrivateRoute><Video360Viewer /></PrivateRoute>
      } />

      {/* Self-hosted 2D viewer (DWG/DXF → SVG, no Autodesk) — the default */}
      <Route path="/projects/:projectId/viewer-2d/:fileId" element={
        <PrivateRoute><DWGViewer /></PrivateRoute>
      } />
      {/* Legacy Autodesk APS 2D viewer — nothing links here, reachable by URL only */}
      <Route path="/projects/:projectId/viewer-2d-v2/:fileId" element={
        <PrivateRoute><DWGViewerV2 /></PrivateRoute>
      } />
      {/* Self-hosted BIM viewer — THE default 3D viewer (IFC/RVT → GLB) */}
      <Route path="/projects/:projectId/viewer-3d-v2/:fileId" element={
        <PrivateRoute><BIMViewerV2 /></PrivateRoute>
      } />
      {/* Legacy Autodesk APS viewer — nothing links here, reachable by URL only */}
      <Route path="/projects/:projectId/viewer-3d/:fileId" element={
        <PrivateRoute><BIMViewer /></PrivateRoute>
      } />
      <Route path="/projects/:projectId/viewer-pdf/:fileId" element={
        <PrivateRoute><PDFViewer /></PrivateRoute>
      } />
      <Route path="/projects/:projectId/viewer-image/:fileId" element={
        <PrivateRoute><ImageViewer /></PrivateRoute>
      } />
      <Route path="/projects/:projectId/viewer-cloud/:fileId" element={
        <PrivateRoute><PointCloudViewer /></PrivateRoute>
      } />
      {/* GeoEngine (our own view engine, experimental) — same file, new renderer */}
      <Route path="/projects/:projectId/viewer-cloud-v2/:fileId" element={
        <PrivateRoute><GeoCloudViewer /></PrivateRoute>
      } />

      <Route path="/projects/:projectId/:viewerType/:fileId" element={
        <PrivateRoute><FileViewer /></PrivateRoute>
      } />

      <Route path="/team" element={
        <PrivateRoute><TeamManagement /></PrivateRoute>
      } />
      <Route path="/profile" element={
        <PrivateRoute><Profile /></PrivateRoute>
      } />
      <Route path="/sharing" element={
        <PrivateRoute><SharingCenter /></PrivateRoute>
      } />
      <Route path="/activity" element={
        <PrivateRoute><ActivityLogs /></PrivateRoute>
      } />
      <Route path="/trash" element={
        <PrivateRoute><Trash /></PrivateRoute>
      } />
      <Route path="/upgrade" element={
        <PrivateRoute><Upgrade /></PrivateRoute>
      } />
      <Route path="/release-notes" element={
        <ReleaseNotesRoute><ReleaseNotes /></ReleaseNotesRoute>
      } />
      <Route path="/release-notes/:version" element={
        <ReleaseNotesRoute><ReleaseNoteDetail /></ReleaseNotesRoute>
      } />

      <Route path="/admin" element={
        <AdminRoute><AdminDashboard /></AdminRoute>
      } />
      <Route path="/admin/projects" element={
        <AdminRoute module="projects"><AdminProjects /></AdminRoute>
      } />
      <Route path="/admin/users" element={
        <AdminRoute module="users"><AdminUsers /></AdminRoute>
      } />
      <Route path="/admin/conversion" element={
        <AdminRoute module="conversion"><AdminConversionJobs /></AdminRoute>
      } />
      <Route path="/admin/costs" element={
        <AdminRoute module="costs"><AdminCostAnalysis /></AdminRoute>
      } />
      <Route path="/admin/cost-calculator" element={
        <AdminRoute module="cost-calculator"><AdminCostCalculator /></AdminRoute>
      } />
      <Route path="/admin/costs/calculator" element={<Navigate to="/admin/cost-calculator" replace />} />
      <Route path="/admin/conversion-stats" element={
        <AdminRoute module="conversion-stats"><AdminConversionStats /></AdminRoute>
      } />
      <Route path="/admin/user-activity" element={
        <AdminRoute module="user-activity"><AdminUserActivity /></AdminRoute>
      } />
      <Route path="/admin/deleted-files" element={
        <AdminRoute module="deleted-files"><AdminDeletedFiles /></AdminRoute>
      } />
      <Route path="/admin/user-plans" element={
        <AdminRoute module="user-plans"><AdminUserPlans /></AdminRoute>
      } />
      <Route path="/admin/scheduled-tasks" element={
        <AdminRoute module="scheduled-tasks"><AdminScheduledTasks /></AdminRoute>
      } />
      <Route path="/admin/settings" element={
        <AdminRoute module="settings"><AdminPlatformSettings /></AdminRoute>
      } />
      <Route path="/admin/release-notes" element={
        <AdminRoute module="release-notes"><AdminReleaseNotes /></AdminRoute>
      } />

      <Route path="*" element={
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center">
            <h1 className="text-6xl font-bold text-primary font-headline mb-4">404</h1>
            <p className="text-on-surface-variant mb-6">Page not found</p>
            <a href="/dashboard" className="text-primary hover:underline font-medium">Back to Dashboard</a>
          </div>
        </div>
      } />
    </Routes>
    </>
  );
};

export default App;
